import type { CentraSelectionItem } from "@frend-digital/centra-legacy/schemas/selection/selectionProductSchema";

import type { CentraProduct } from "./atomicSetup";
import { baseAtomics } from "./atomicSetup";

import {
  getSelectionItemLine,
  getSelectionItemName,
  getSelectionItemPriceEach,
  getSelectionItemPriceEachAsNumber,
  getSelectionItemPriceEachBeforeDiscountAsNumber,
  getSelectionItemQuantity,
  getSelectionItemSize,
  getSelectionItemUri,
  getSelectionItemVariantName,
} from "./selectionAtoms";

const {
  getMediaBySize,
  getMeasurementChart,
  getProductBreadcrumbs,
  getProductSizes,
} = baseAtomics;

const formatMeasurementChart = (chart: {
  contents: { x: number; y: number; content: string }[];
  x: string[];
  y: string[];
}) => {
  const { contents, x, y } = chart;
  const formattedChart = [];

  for (let i = 0; i < y.length; i++) {
    const row = { size: y[i] } as {
      size: string;
      [measurement: string]: string;
    };
    for (let j = 0; j < x.length; j++) {
      const measurement = x[j];
      const value = contents.find(
        (content: { x: number; y: number }) =>
          content.x === j && content.y === i
      )?.content;
      if (value) {
        row[measurement] = value;
      }
    }
    formattedChart.push(row);
  }

  return { chart: formattedChart, keys: x };
};

export const getSizeGuide = (product: CentraProduct) => {
  const measurementChart = getMeasurementChart(product);
  const sizeGuide = formatMeasurementChart(measurementChart);
  return sizeGuide;
};

export const getComingSoon = (product: CentraProduct) => {
  const comingSoon = product?.pr_coming_soon_value;

  return comingSoon;
};

export const getSoldOut = (product: CentraProduct) => {
  const sizes = getProductSizes(product);
  const soldOut = sizes?.every((size) => size.stock === false);

  return soldOut;
};

export const getType = (
  product: CentraProduct | CentraSelectionItem["product"]
) => {
  const type = product?.pr_product_type_type;
  return type;
};

export const getSwatch = (product: CentraProduct) => {
  const swatch = product.pr_color;
  return swatch;
};

export const getComposition = (product: CentraProduct) => {
  const composition = product.pr_composition_value;
  return composition;
};

export const getCare = (product: CentraProduct) => {
  const care = product.pr_garment_care;
  return care;
};

export const getFitAdvice = (product: CentraProduct) => {
  const fit = product.pr_fit_advice;
  return fit;
};

export const getSustainability = (product: CentraProduct) => {
  const sustainability = product.pr_sustainability;
  return sustainability;
};

const getVideo = (product: CentraProduct) => {
  const video = product.pr_video_value;
  if (!video) return null;
  return {
    type: "video",
    href: video,
    alt: "",
  };
};

export const getFullImage = (product: CentraProduct) =>
  getMediaBySize(product, "large-2560").map((media) => ({
    type: "image",
    ...media,
  }));

export const getMediaWithVideo = (product: CentraProduct) => {
  const media = getFullImage(product);
  const video = getVideo(product);
  const mediaLength = media.length;
  const mediaWithVideo = video
    ? [...media.slice(0, 2), video, ...media.slice(2, mediaLength)]
    : media;
  return mediaWithVideo;
};

export const getFirstImage = (item: CentraSelectionItem) => {
  const mediaData = Object.keys(item?.product?.media);

  let media = mediaData?.map((key) => {
    return { href: item?.product?.media[key]?.[0], alt: "" };
  });
  if (!media) return null;
  return media;
};

export const getFirstThumbnail = (item: CentraSelectionItem) => {
  const firstThumbnail = item?.product?.media?.["700x0"]?.[0];
  return firstThumbnail;
};

const offsetYarnCount = (yarns: number | string) => {
  yarns = Number(yarns);
  if (isNaN(yarns)) return 4;
  // Centra requires us to have the Centra config set 1 higher than the actual amount of yarns required.
  // See https://frend-digital-as.slack.com/archives/C017YDGQ17Z/p1709278894858609?thread_ts=1709278133.435179&cid=C017YDGQ17Z
  return yarns - 1;
};

export const getRequiredYarns = (item: CentraSelectionItem) => {
  const requiredYarns = item?.product?.pr_yarns_required_value;
  return offsetYarnCount(requiredYarns);
};

export const generateCartItem = (item: CentraSelectionItem) => {
  const name = getSelectionItemName(item) ?? "";
  const uri = getSelectionItemUri(item) ?? "";
  const variant = getSelectionItemVariantName(item) ?? "";
  const size = getSelectionItemSize(item) ?? "";
  const id = getSelectionItemLine(item) ?? "";
  const price = getSelectionItemPriceEach(item);
  const discountedPrice = getSelectionItemPriceEach(item);
  const priceBeforeDiscount =
    getSelectionItemPriceEachBeforeDiscountAsNumber(item);
  const firstThumbnail = getFirstThumbnail(item);
  const quantity = getSelectionItemQuantity(item) ?? 0;
  const media = getFirstImage(item);
  const priceAsNumber = getSelectionItemPriceEachAsNumber(item);
  const comment = item?.comment ?? "";
  const line = item.line ?? "";
  const type = getType(item.product);
  const requiredYarns = getRequiredYarns(item);

  return {
    name,
    uri,
    variant,
    size,
    id,
    price,
    priceAsNumber,
    quantity,
    media,
    comment,
    type,
    discountedPrice,
    priceBeforeDiscount,
    line,
    firstThumbnail,
    requiredYarns,
  };
};

export type GeneratedCartItem = ReturnType<typeof generateCartItem>;

export const getIsNew = (item: CentraProduct) => {
  if ("pr_new" in item) return item.pr_new as "1";
  else return;
};
